import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import { log } from '@/services/Log'
import type { StandardAddress } from '@/types/ShopFront/CheckoutStandards'
import type { AutocompletePrediction } from '@/types/ThirdPartyIntegrations/Maps'

type MinimumPropsToPredictAddress = {
  stateAcronym: string,
  street1: string
  street2: string
  city: string
  zip: string
  email?: string
  country?: string
  customFields?: StandardAddress['customFields']
  firstName?: string
  lastName?: string
  phone?: string
  addressType?: string
  shouldSaveAddress?: boolean
}

const isMatchingAddress = (addressA: StandardAddress) => (addressB: StandardAddress) => (
  JSON.stringify(addressB) === JSON.stringify(addressA)
)

const removeDuplicatedAddresses = (addresses: StandardAddress[]) => (
  addresses.reduce((acc, addressA) => (
    acc.find(isMatchingAddress(addressA))
      ? acc
      : [...acc, addressA]
  ), [] as StandardAddress[])
)

const isValidAutoCompletePrediction = ({ types }: AutocompletePrediction) => types?.includes('street_address')

type PlacesPredictionResponse = {
  success: boolean
  validAddress: boolean
  predictions: AutocompletePrediction[] | null | undefined
  addressPredictions: StandardAddress[]
}
export const getPlacesPredictions = async <T extends MinimumPropsToPredictAddress>(input: T)
: Promise<PlacesPredictionResponse> => {
  //  There are two API sources for verification
  //  if no address is found we show the no results page,
  //  if we found but none is equal to the one the user provided, then we show suggestions,
  //  if we found at least one matching the user input we assume its correct and continue
  log('getPlacesPredictions START', { ...input })

  const [
    { isSameAddress, addressHasRequiredFields },
    { placesAPIPresent, getNonShippedStates },
    { getAddressObjectFromDescription },
    { addGooglePlacesAPI },
  ] = await allPromisesWithRetries(() => [
    import('@/helpers/addressHelpers'),
    import('.'),
    import('./getAddressObjectFromDescription'),
    import('./addGooglePlacesAPI'),
  ])

  const nonShippedStates = getNonShippedStates()
  const addressInDeliverableState = (
    (address: { stateAcronym :string }) => !nonShippedStates.includes(address.stateAcronym)
  )

  const invalidResult = (errorMessage: string) => ({
    success: false,
    validAddress: false,
    predictions: [],
    addressPredictions: [],
    errorMessage,
  })
  if (!placesAPIPresent()) {
    try {
      await addGooglePlacesAPI()
    } catch {
      return invalidResult('maps API failed to load script')
    }
  }
  let { street1 } = input
  if (!input.street2) {
    // Checking if line 2 has been added as part of line 1
    const unitNumberPattern = /(bsmt|basement)$|(((APT|APARTMENT|UNIT|#|SUITE)*)([.\s]*)((number|num|#|n)*)([\s]*))([0-9/]+)$/gim
    const matches = unitNumberPattern.exec(input.street1)
    if (matches !== null && matches.length) {
      const split = `${input.street1}`.split(unitNumberPattern)
      street1 = `${(split?.[0] || input.street1)}`.trim()
    }
  }
  const inputString = [street1, input.city, input.stateAcronym, input.zip].reduce(
    (fullAddress, addrChunk) => `${fullAddress}${addrChunk ? `${fullAddress ? ', ' : ''}${addrChunk}` : ''}`,
  )
  // Concat Address into a string
  if (!inputString || !placesAPIPresent()) {
    return invalidResult('Invalid input')
  }
  const buildAddrWithInput = (address: StandardAddress): StandardAddress => ({
    ...input,
    ...address,
    country: input.country || address.country,
    customFields: input.customFields || address.customFields,
    email: input.email || '',
    firstName: input.firstName || '',
    lastName: input.lastName || '',
    phone: input.phone || '',
    addressType: input.addressType || address.addressType,
    shouldSaveAddress: !!input.shouldSaveAddress,
    street2: input.street2
      ? input.street2
      : address.street2,
  })
  const {
    success, validAddress, predictions, addressPredictions,
  } = (
    await new Promise<PlacesPredictionResponse>((resolve) => {
      (new window.google.maps.places.AutocompleteService())
        .getPlacePredictions(
          { input: inputString, types: ['address'], componentRestrictions: { country: ['us'] } },
          async (placesPredictions) => {
            // Fetching Suggestions for the input address
            const inputPredictionsFromPlacesGetPlace = await getAddressObjectFromDescription({
              description: inputString,
            })
            const tempArray = inputPredictionsFromPlacesGetPlace.map(buildAddrWithInput)
            const placesAddressesQueries = (
              // Places query insertion, some addresses are valid but don't return predictions
              placesPredictions
                ? (
                  [...placesPredictions]
                    .filter(isValidAutoCompletePrediction)
                    .map(getAddressObjectFromDescription)
                )
                : []
            )
            const placesResolvedAddressesArray = await Promise.all(placesAddressesQueries)
            const unfilteredAddressesFromMaps = (
              placesResolvedAddressesArray.reduce((acc, descriptionPredictedAddressReturned) => (
                [...acc, ...descriptionPredictedAddressReturned.map(buildAddrWithInput)]
              ), tempArray)
            )
            const addressesPredictionsArray = (
              removeDuplicatedAddresses(unfilteredAddressesFromMaps)
                .filter(addressHasRequiredFields)
                .filter(addressInDeliverableState)
            )
            return resolve({
              success: addressesPredictionsArray.length > 0,
              validAddress: (
                // Has a prediction with a perfect match
                !!addressesPredictionsArray.find((addr) => (
                  isSameAddress(addr, input)
                ))
              ),
              predictions: placesPredictions,
              addressPredictions: addressesPredictionsArray,
            })
          },
        )
    })
  )
  log('getPlacesPredictions END', {
    success, validAddress, predictions, addressPredictions,
  })
  return {
    success: !!success,
    validAddress: !!validAddress,
    predictions,
    addressPredictions,
  }
}

export default getPlacesPredictions
